import { useEffect, useRef, type ReactNode } from 'react'
import { CheckCircle2 } from 'lucide-react'
import {
  MessageScrollerProvider,
  MessageScroller,
  MessageScrollerViewport,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerButton,
} from '../ui/message-scroller'
import { Message, MessageContent } from '../ui/message'
import { Bubble, BubbleContent } from '../ui/bubble'
import { Marker, MarkerIcon, MarkerContent } from '../ui/marker'
import { Markdown } from '../ui/markdown'
import { ActionProposalCard } from './action-proposal-card'
import { formatToolName, getMessageText, QUESTIONNAIRE_TOOL_NAME } from './chat-utils'
import { EntityCardList } from './entity-cards/entity-card-list'
import { RENDER_DATA_TOOL_NAME, parseRenderDataResult } from './entity-cards/render-data-payload'
import type { Message as ChatMessage, ToolExecution } from '@/contexts/ai-assistant-context'
import { useAIAssistant } from '@/contexts/ai-assistant-context'
import { cn } from '@/lib/utils'
import { AgentActivityGlyph } from './agent-activity-glyph'
import { AgentQuestionnaireCard } from './agent-questionnaire-card'
import { getToolActivity } from './ai-assistant-status'
import { useSmoothStreamText } from './use-smooth-stream-text'
import { UsedForThisAnswer } from './used-for-this-answer'

/**
 * Assistant replies read as page text rather than chat chrome: no bubble, full
 * row width, and explicit markdown block rhythm — Tailwind's preflight zeroes
 * every heading/paragraph/list margin, so spacing has to be restated here.
 */
const assistantTextClassName = cn(
  'min-w-0 space-y-3 text-sm leading-relaxed wrap-break-word',
  '[&_h1]:text-base [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold',
  '[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:my-1',
  '[&_a]:font-medium [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-primary',
  '[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground',
  '[&_hr]:border-border',
  '[&_table]:w-full [&_table]:text-left [&_th]:border-b [&_th]:border-border [&_th]:pb-1 [&_th]:font-medium [&_td]:border-b [&_td]:border-border/50 [&_td]:py-1',
)

interface ChatTranscriptProps {
  messages: ChatMessage[]
  emptyState?: ReactNode
  className?: string
  contentClassName?: string
}

/**
 * Shared transcript UI rendered by both the AI assistant sidebar and the full
 * `/chat` page. Built on the shadcn MessageScroller/Message/Bubble/Marker
 * primitives — this is the one component tree both containers wrap in their
 * own chrome (header, history list, composer).
 */
export function ChatTranscript({ messages, emptyState, className, contentClassName }: ChatTranscriptProps) {
  const {
    pendingActions,
    approveAction,
    rejectAction,
    questionnaires,
    submitQuestionnaire,
    disclosures,
    fetchDisclosure,
    isLoadingDisclosure,
  } = useAIAssistant()

  // Entrance animation should only play for rows added after this transcript
  // first painted — not for an existing conversation's full history. Track
  // which message ids were already present at mount (and adopt every id we
  // render, post-mount, as "seen") so only genuinely new rows are flagged.
  const seenMessageIdsRef = useRef<Set<string>>(new Set())
  const hasMountedRef = useRef(false)
  const newMessageIds = hasMountedRef.current
    ? new Set(messages.filter((message) => !seenMessageIdsRef.current.has(message.id)).map((message) => message.id))
    : new Set<string>()

  useEffect(() => {
    hasMountedRef.current = true
    for (const message of messages) seenMessageIdsRef.current.add(message.id)
  }, [messages])

  if (messages.length === 0 && emptyState) {
    return <>{emptyState}</>
  }

  return (
    <MessageScrollerProvider autoScroll defaultScrollPosition="end">
      <MessageScroller className={className}>
        <MessageScrollerViewport>
          <MessageScrollerContent role="log" className={contentClassName}>
            {messages.map((message, index) => (
              <MessageScrollerItem key={message.id} scrollAnchor={index === messages.length - 1}>
                <ChatMessageRow
                  message={message}
                  isNew={newMessageIds.has(message.id)}
                  pendingActions={pendingActions}
                  onApprove={approveAction}
                  onReject={rejectAction}
                  questionnaires={questionnaires}
                  onSubmitQuestionnaire={submitQuestionnaire}
                  disclosures={disclosures}
                  fetchDisclosure={fetchDisclosure}
                  isLoadingDisclosure={isLoadingDisclosure}
                />
              </MessageScrollerItem>
            ))}
          </MessageScrollerContent>
        </MessageScrollerViewport>
        <MessageScrollerButton />
      </MessageScroller>
    </MessageScrollerProvider>
  )
}

function ChatMessageRow({
  message,
  isNew,
  pendingActions,
  onApprove,
  onReject,
  questionnaires,
  onSubmitQuestionnaire,
  disclosures,
  fetchDisclosure,
  isLoadingDisclosure,
}: {
  message: ChatMessage
  isNew: boolean
  pendingActions: ReturnType<typeof useAIAssistant>['pendingActions']
  onApprove: (proposalId: string) => Promise<void>
  onReject: (proposalId: string) => Promise<void>
  questionnaires: ReturnType<typeof useAIAssistant>['questionnaires']
  onSubmitQuestionnaire: ReturnType<typeof useAIAssistant>['submitQuestionnaire']
  disclosures: ReturnType<typeof useAIAssistant>['disclosures']
  fetchDisclosure: ReturnType<typeof useAIAssistant>['fetchDisclosure']
  isLoadingDisclosure: ReturnType<typeof useAIAssistant>['isLoadingDisclosure']
}) {
  const isUser = message.role === 'user'
  const rowClassName = cn(isNew && 'animate-message-enter')
  // The full text across every part, in order — the source of truth for how
  // much has streamed in. `revealedText` is always a prefix of it, so each
  // part's on-screen slice is just that prefix cut at the part's own offset.
  const fullText = getMessageText(message.parts)
  // View-layer smoothing only: `fullText` stays the full source of truth,
  // this just paces how much of it is painted. Inert for user rows, which
  // never stream.
  const revealedText = useSmoothStreamText(fullText, message.isStreaming ?? false)

  if (isUser) {
    return (
      <Message align="end" className={rowClassName}>
        <MessageContent>
          <Bubble align="end">
            <BubbleContent className="whitespace-pre-wrap">{fullText}</BubbleContent>
          </Bubble>
        </MessageContent>
      </Message>
    )
  }

  const proposals = (message.proposalIds ?? []).map((id) => pendingActions[id]).filter((p) => p !== undefined)
  const messageQuestionnaires = (message.questionnaireIds ?? []).map((id) => questionnaires[id]).filter((q) => q !== undefined)
  const hasToolPart = message.parts.some((part) => part.type === 'tool')
  // Gated on the revealed text, not the raw content: the thinking trace has to
  // hold until real characters land, or the marker swaps out for an empty
  // text region the instant the first chunk arrives.
  const isThinking = message.isStreaming && !revealedText && !hasToolPart
  const isRevealing = Boolean(message.isStreaming) || revealedText.length < fullText.length

  let textConsumed = 0

  return (
    <Message align="start" className={rowClassName}>
      <MessageContent>
        {message.parts.map((part, index) => {
          if (part.type === 'tool') {
            const tool = part.tool
            // The questionnaire has its own card (rendered below via
            // `questionnaireIds`); skip the generic marker so the tool call
            // backing it doesn't duplicate that card.
            if (tool.toolName === QUESTIONNAIRE_TOOL_NAME) return null
            if (tool.toolName === RENDER_DATA_TOOL_NAME && tool.status === 'completed') {
              const payload = parseRenderDataResult(tool.resultJson)
              if (payload) return <EntityCardList key={tool.toolId} payload={payload} />
            }
            return <ToolActivityMarker key={tool.toolId} tool={tool} />
          }

          const start = textConsumed
          textConsumed += part.text.length
          const revealedPart = revealedText.slice(start, textConsumed)
          if (!revealedPart) return null
          return (
            <div key={`text-${index}`} data-slot="assistant-message-text" data-streaming={isRevealing ? 'true' : undefined} className="min-w-0">
              <Markdown className={assistantTextClassName}>{revealedPart}</Markdown>
            </div>
          )
        })}

        {isThinking && (
          <Marker>
            <MarkerIcon>
              <AgentActivityGlyph state="thinking" className="text-primary" />
            </MarkerIcon>
            <MarkerTitleDetail title="Thinking…" />
          </Marker>
        )}

        {proposals.length > 0 && (
          <div className="space-y-2">
            {proposals.map((proposal) => (
              <ActionProposalCard key={proposal.id} proposal={proposal} onApprove={onApprove} onReject={onReject} />
            ))}
          </div>
        )}

        {messageQuestionnaires.length > 0 && (
          <div className="flex flex-col gap-2">
            {messageQuestionnaires.map((questionnaire) => (
              <AgentQuestionnaireCard
                key={questionnaire.id}
                questionnaire={questionnaire}
                onSubmit={onSubmitQuestionnaire}
              />
            ))}
          </div>
        )}

        {!message.isStreaming && (
          <UsedForThisAnswer
            turnId={message.turnId}
            disclosure={message.turnId ? disclosures[message.turnId] : undefined}
            isLoading={message.turnId ? isLoadingDisclosure(message.turnId) : false}
            onExpand={() => {
              if (message.turnId) void fetchDisclosure(message.turnId)
            }}
          />
        )}
      </MessageContent>
    </Message>
  )
}

function ToolActivityMarker({ tool }: { tool: ToolExecution }) {
  const isRunning = tool.status === 'running'
  const activityState = getToolActivity(tool.toolName).state

  return (
    <Marker role={isRunning ? 'status' : undefined}>
      <MarkerIcon>
        {isRunning ? <AgentActivityGlyph state={activityState} className="text-primary" /> : <CheckCircle2 className="text-green-600 dark:text-green-400" />}
      </MarkerIcon>
      <MarkerTitleDetail title={formatToolName(tool.toolName)} />
    </Marker>
  )
}

/**
 * Two-line marker body: a title plus an optional muted detail sub-line. The
 * agent stream doesn't currently emit a free-text
 * "current step" description for either the generic thinking marker or tool
 * executions, so no caller passes `detail` today — the component degrades to
 * a single line rather than rendering an empty second line. This exists so a
 * future stream event carrying real step text has somewhere to go without
 * another markup change.
 */
function MarkerTitleDetail({ title, detail }: { title: string; detail?: string }) {
  return (
    <MarkerContent className="flex min-w-0 flex-col gap-0.5">
      <span className="truncate">{title}</span>
      {detail && <span className="truncate text-xs text-muted-foreground/70">{detail}</span>}
    </MarkerContent>
  )
}
